/**
 * Field mapping for "Duplicate flight" (single flight, same campaign).
 *
 * The SAME rule is implemented server-side for "Duplicate campaign" in
 * public._duplicate_campaign_impl (migration 2026-10-14-481). Keep the two in step:
 * a key added to FLIGHT_COPY_METADATA_KEYS must be added to the migration's allowlist.
 *
 * Rule (2026-10-14, flight-duplicate-fidelity):
 *  - COPY what the operator configured: content, template, offer, voucher switch,
 *    email category, IP pool, SMS settings, and the flight-form metadata keys below.
 *  - RESET everything the system wrote while sending: status, schedule, counters,
 *    leases, snapshots, fingerprints, EIB identity, and every other metadata key
 *    (send stats, recovery notes, the audience-exclusion breakdown frozen at send,
 *    voucher-generation report, G3 pre-send stamps, ...). Metadata is an ALLOWLIST:
 *    a key not listed here is not copied.
 *  - SEGMENT (list_id), owner decision 2026-10-15 (option A): a copy keeps the original's
 *    segment, EXCEPT a voucher flight (generate_vouchers) starts with none (2026-06-29
 *    incident: a copy minted vouchers for the old segment). A REMINDER has no segment of
 *    its own: it inherits its voucher source's current segment, exactly as the flight
 *    form does on save. No segment on the original -> none on the copy.
 *    EXCEPTION (owner decision 2026-10-15): a copy from an Event in a Box campaign starts with
 *    NO segment (eibCampaign), whatever the flight. Mirrors migration 488.
 *  - REMINDER link (voucher_source_flight_id): copied — a duplicated reminder stays a
 *    reminder of the same source flight (same campaign), and never generates vouchers.
 */
import type { CampaignFlight, CreateFlightDTO } from "../types/campaign.types";

/**
 * Metadata keys the flight form writes (FlightForm submit). These are operator
 * configuration and are carried onto a copy; nothing else in metadata is.
 */
export const FLIGHT_COPY_METADATA_KEYS = [
  "send_timezone",
  "preheader",
  "from_name",
  "from_email",
  "reply_to",
  // {{assigned_agent}} fallbacks (mig 1550; also in _duplicate_campaign_impl)
  "from_fallback_email",
  "reply_to_fallback",
  // Reply-To display name (2026-11-04, mig 1600; also in _duplicate_campaign_impl)
  "reply_to_name",
  "landing_page_config",
  "audience_filters",
  "exclude_risky",
  "apply_tag",
  "remove_tag",
  "reminder_include_new_members",
  "retarget_flight_id",
  "retarget_exclude_event",
] as const;

export function pickFlightCopyMetadata(
  md: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined {
  if (!md || typeof md !== "object" || Array.isArray(md)) {
    return undefined;
  }
  const out: Record<string, unknown> = {};
  for (const k of FLIGHT_COPY_METADATA_KEYS) {
    if (Object.prototype.hasOwnProperty.call(md, k) && md[k] !== undefined) {
      out[k] = md[k];
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export type DuplicateFlightSource = Pick<
  CampaignFlight,
  | "campaign_id"
  | "list_id"
  | "name"
  | "description"
  | "template_id"
  | "generate_vouchers"
  | "voucher_source_flight_id"
  | "parameter_overrides"
  | "offer_id"
  | "email_template_registry_id"
  | "email_subject"
  | "email_template_id"
  | "email_template_data"
  | "subscription_group_id"
  | "sendgrid_ip_pool_key"
  | "sms_template_id"
  | "sms_message_override"
  | "metadata"
> & {
  send_email?: boolean | null;
  send_sms?: boolean | null;
};

export type DuplicateFlightDTO = CreateFlightDTO & {
  send_email?: boolean | null;
  send_sms?: boolean | null;
  /** The user who duplicated the flight (owner decision 2026-10-14). */
  created_by?: string | null;
};

/**
 * Build the insert for a single-flight duplicate.
 *
 * @param reminderSourceListId for a reminder (voucher_source_flight_id set): the
 *   CURRENT list_id of its voucher source flight, which the copy inherits. Ignored
 *   for ordinary flights.
 * @param createdBy the signed-in user making the copy (session user id); the copy's
 *   created_by. Never taken from the source row.
 */
export function buildDuplicateFlightDTO(
  source: DuplicateFlightSource,
  reminderSourceListId?: string | null,
  createdBy?: string | null,
  opts?: { eibCampaign?: boolean },
): DuplicateFlightDTO {
  const isReminder = Boolean(source.voucher_source_flight_id);
  return {
    campaign_id: source.campaign_id,
    name: `${source.name} (Copy)`,
    description: source.description,
    list_id: opts?.eibCampaign
      ? undefined
      : isReminder
        ? reminderSourceListId || undefined
        : source.generate_vouchers
          ? undefined
          : source.list_id || undefined,
    template_id: source.template_id,
    // A reminder reuses its source's codes and never mints (FlightForm forces false).
    generate_vouchers: isReminder ? false : Boolean(source.generate_vouchers),
    voucher_source_flight_id: isReminder
      ? source.voucher_source_flight_id
      : null,
    parameter_overrides: source.parameter_overrides,
    offer_id: source.offer_id,
    email_template_registry_id: source.email_template_registry_id,
    email_subject: source.email_subject,
    email_template_id: source.email_template_id,
    email_template_data: source.email_template_data,
    subscription_group_id: source.subscription_group_id ?? null,
    sendgrid_ip_pool_key: source.sendgrid_ip_pool_key ?? "default",
    sms_template_id: source.sms_template_id,
    sms_message_override: source.sms_message_override,
    send_email: source.send_email ?? false,
    send_sms: source.send_sms ?? false,
    created_by: createdBy ?? null,
    metadata: pickFlightCopyMetadata(
      source.metadata as Record<string, unknown> | null | undefined,
    ),
    // scheduled_send_at intentionally omitted: a copy is always an unscheduled draft.
  };
}
